import React, { useState } from 'react';
import { PlacesType, Tooltip } from 'react-tooltip';
import { randomString } from '/imports/utils';

// The .primary / .secondary / .tertiary / .compact / .caps / .medium class-name
// contract is load-bearing: 32 files pass these as raw className strings and
// AutoForm/DividerTabs append `!`-important overrides on top. Restyle inside,
// never rename.
//
// `capitalize` is gone on purpose. The console reserves uppercase for eyebrows,
// table headers and chip labels; capitalising buttons as well destroys that
// contrast. Icons inherit the button's text color (Icon defaults to
// currentColor now), so no per-variant icon overrides are needed.
const buttonClassName = [
  'btn-base relative inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border-none px-3.5 font-sans text-[13px] font-semibold',
  'h-9 shadow-chunky-sm [transition:background_0.15s_ease,color_0.15s_ease,border-color_0.15s_ease]',
  'focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:outline-none',
  // primary: the emerald fill. text-on-primary flips to dark ink on obsidian,
  // where white would fail contrast against the lighter lime.
  '[&.primary]:border [&.primary]:border-primary [&.primary]:bg-primary [&.primary]:text-on-primary',
  '[&.primary:hover]:border-primary-2 [&.primary:hover]:bg-primary-2',
  '[&.primary:disabled]:cursor-not-allowed [&.primary:disabled]:border [&.primary:disabled]:border-grey-5 [&.primary:disabled]:bg-grey-5 [&.primary:disabled]:text-grey-3 [&.primary:disabled]:opacity-75',
  '[&.primary:disabled:hover]:bg-grey-5',
  // secondary: the default surface button, a panel fill on a hairline edge.
  '[&.secondary]:border [&.secondary]:border-soft [&.secondary]:bg-panel [&.secondary]:text-fg-1',
  '[&.secondary:hover]:bg-page-alt [&.secondary:hover]:border-mid',
  '[&.secondary:disabled]:cursor-not-allowed [&.secondary:disabled]:bg-grey-8 [&.secondary:disabled]:text-grey-4 [&.secondary:disabled]:opacity-75',
  '[&.secondary:disabled:hover]:bg-grey-8 [&.secondary:disabled:hover]:text-grey-4',
  // tertiary: outline only, no fill.
  '[&.tertiary]:border [&.tertiary]:border-mid [&.tertiary]:bg-transparent [&.tertiary]:text-fg-2',
  '[&.tertiary:hover]:border-primary [&.tertiary:hover]:text-primary',
  '[&.tertiary:disabled]:cursor-not-allowed [&.tertiary:disabled]:opacity-75',
  '[&.tertiary:disabled:hover]:border-mid [&.tertiary:disabled:hover]:text-fg-2',
  // size / weight modifiers. rounded-md, not rounded-xl: pill-shaped chips are
  // the generic-SaaS tell the console rejects.
  '[&.compact]:h-7 [&.compact]:rounded-md [&.compact]:px-2.5 [&.compact]:text-[12px]',
  '[&.caps]:uppercase [&.caps]:tracking-caps',
  '[&.medium]:font-semibold',
].join(' ');

interface ButtonProps {
  id?: string,
  className?: string,
  style?: React.CSSProperties,
  type?: 'button' | 'submit',
  disabled?: boolean,
  onClick?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void,
  onDoubleClick?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void,
  children?: React.ReactNode,
  tip?: string,
  tipPlacement?: PlacesType,
  'data-tooltip-id'?: string;
  tipEvents?: {
    mouseenter?: boolean
    focus?: boolean
    mouseover?: boolean
    click?: boolean
    dblclick?: boolean
    mousedown?: boolean
  };
}

export const Button = (props: ButtonProps) => {
  const className = `btn ${props.className || 'primary'}`;
  const [tipId,] = useState(props['data-tooltip-id'] || `tip${randomString()}`);
  return (
    <>
      <button
        id={props.id}
        className={`${buttonClassName} ${className}`}
        style={props.style}
        type={props.type || 'button'}
        disabled={props.disabled}
        onClick={props.onClick}
        onDoubleClick={props.onDoubleClick}
        data-tooltip-id={tipId}
      >
        {props.children || null}
      </button>
      {
        props.tip ?
          <Tooltip id={tipId} content={props.tip} place={props.tipPlacement} openEvents={props.tipEvents || { mouseover: true }} />
          :
          null
      }
    </>
  );
};